import { GenericAbortSignal } from 'axios';

import { filtersForSelectedTab } from 'components/portfolio/helpers/filters';
import { PortfolioTab } from 'components/portfolio/Portfolio';
import { NONE, PHYSICIAN_TEAM } from 'constants/filterKeysConstants';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import LocationEpisode from 'models/LocationEpisode';
import Profile from 'models/Profile';
import { indexLocationEpisodes } from 'services/api/locationEpisodes';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';
import { PortfolioFilterState } from 'stores/portfolioStore';

export type PortfolioColumn = {
  data: LocationEpisode[];
  meta: { totalPages: number; totalRecords: number };
};

type LaneResponse = {
  data: Record<string, any>[];
  meta: { totalPages: number; totalRecords: number };
};

export type PortfolioFilter = {
  key: string;
  options: { id: string; name: string }[];
  isAttrValueStyle: boolean;
};

function mapPortfolioResponseToColumn(y: LaneResponse): PortfolioColumn {
  return {
    data: y.data.map((x) => new LocationEpisode(x)),
    meta: y.meta,
  };
}

type PortfolioResponse = {
  queue: LaneResponse;
  discharged: LaneResponse;
  inTreatment: LaneResponse;
};

type IndexParams = {
  locationType?: string;
  patientState?: string;
  filters?: PortfolioFilterState;
  currentRehabState?: string;
  active?: boolean;
  include?: string;
} & BaseIndexQueryParams;

function serializeFilters(filters: PortfolioFilterState) {
  return Object.entries(filters).reduce((acc, [key, value]) => {
    if (value instanceof FilterValueArray) {
      if (!value) return acc;

      if (key === PHYSICIAN_TEAM) {
        acc[key] = value.map((x) => (x.name === NONE ? x.id : x.name));
      } else {
        acc[key] = value.map((x) => x.id).join(',');
      }
    } else if (value instanceof FilterValueBoolean) {
      if (value.value) {
        acc[key] = value.value;
      }
    }
    return acc;
  }, {});
}

export async function indexPortfolio({ filters, ...params }: IndexParams, signal: GenericAbortSignal) {
  const serializedParams = {
    ...params,
    ...serializeFilters(filters ?? {}),
  };

  return (
    http
      // When currentRehabstate=All param is passed, the response is a PortfolioResponse, which has the data
      // separated by lane. Otherwise it is a normal index response, which conforms to a single LaneResponse
      .get<PortfolioResponse | LaneResponse>('/location_episodes/portfolio', { params: serializedParams, signal })
      .then((res) => {
        if ('queue' in res.data) {
          return {
            queue: mapPortfolioResponseToColumn(res.data.queue),
            inTreatment: mapPortfolioResponseToColumn(res.data.inTreatment),
            discharged: mapPortfolioResponseToColumn(res.data.discharged),
          };
        } else {
          return mapPortfolioResponseToColumn(res.data);
        }
      })
  );
}

export async function indexPortfolioLane({ filters, ...params }: IndexParams, signal: GenericAbortSignal) {
  const serializedParams = {
    ...params,
    ...serializeFilters(filters ?? {}),
  };

  return http.get<LaneResponse>('/location_episodes/portfolio', { params: serializedParams, signal }).then((res) => {
    return mapPortfolioResponseToColumn(res.data);
  });
}

type PatientCountParams = {
  profile: Profile;
  tab: PortfolioTab;
  filters: PortfolioFilterState;
  search: string;
};

export async function getPatientCounts(
  { profile, tab, filters, search }: PatientCountParams,
  signal: GenericAbortSignal
) {
  const filtersForSelectedGroupType = filtersForSelectedTab(filters, tab as PortfolioTab, profile);

  return indexLocationEpisodes(
    {
      locationType: tab.locationType,
      patientState: tab.patientState,
      active: true,
      pageSize: 1,
      search,
      ...serializeFilters(filtersForSelectedGroupType),
    },
    signal
  );
}

export const portfolioQueryKeys = {
  index: ['portfolio'],
  count: (locationTypeOrPatientState: string) => ['portfolio', 'count', locationTypeOrPatientState],
  lane: (lane: string) => ['portfolio', lane],
};
